export default class MetaTags {
  constructor(asset, site, siteMap, t) {
    this.asset = asset;
    this.site = site;
    this.siteMap = siteMap;
    this.t = t;
  }

  isFilm() {
    return this.asset.assetType === 'film';
  }

  isEditorial() {
    return this.asset.family === 'editorial';
  }

  itemProp() {
    return this.isFilm() ? 'video' : 'image';
  }

  itemType() {
    return this.isFilm()
      ? 'http://schema.org/VideoObject'
      : 'http://schema.org/ImageObject';
  }

  seoTitle() {
    if (this.site.isGetty() && this.isEditorial()) {
      const descriptor = this.isFilm()
        ? this.t('gallery.video_footage')
        : this.t('news_photo');
      return `${this.truncateEditorialTitle()} ${descriptor}`;
    }
    if (this.site.isIstock()) {
      return this.istockTitle();
    }
    return `${this.asset.seoTitle} ${this.mediaDescription()}`;
  }

  seoDescription() {
    if (this.site.isIstock()) {
      return this.istockSeoDescription({ asset_title: this.asset.seoTitle });
    }
    return this.gettySeoDescription();
  }

  seoCaptionOrTitle() {
    return this.asset.seoCaption || this.asset.seoTitle;
  }

  truncateEditorialTitle() {
    return this.truncateText(this.seoCaptionOrTitle(), 70);
  }

  truncateEditorialDescription() {
    if (!this.sanitized200) {
      this.sanitized200 = this.truncateText(this.seoCaptionOrTitle(), 200);
    }
    return this.sanitized200;
  }

  gettySeoDescription() {
    if (this.isFilm()) {
      if (this.isEditorial()) {
        return this.t('site_specific.getty.footage_of', {
          caption: this.truncateEditorialDescription(),
        });
      }
      return this.t('site_specific.getty.stock_footage_of', {
        caption: this.truncateEditorialTitle(),
      });
    }
    if (this.isEditorial()) {
      return `${this.truncateEditorialDescription()} ${this.t(
        'get_premium_news_photos',
        { site_name: this.site.name }
      )}`;
    }
    return this.t(`site_specific.${this.site.nickname}.seo_meta_description`, {
      asset_title: this.asset.seoTitle,
      asset_type_single: this.seoAssetType(),
    });
  }

  istockSeoDescription(translationParams) {
    if (this.isFilm()) {
      return this.t(
        'site_specific.istock.istock_video_seo_description',
        translationParams
      );
    }

    switch (this.asset.mediaType.toLowerCase()) {
      case 'photography':
      case 'photo':
        return this.t(
          'site_specific.istock.istock_photo_seo_description',
          translationParams
        );

      case 'illustration':
      case 'photo_and_illustration':
      case 'photography_and_illustration':
        return this.t(
          'site_specific.istock.istock_vector_seo_description',
          translationParams
        );
      default:
        return '';
    }
  }

  stockMediaTranslationKey() {
    switch (this.asset.mediaType.toLowerCase()) {
      case 'photography':
      case 'photo':
        return 'stock_photo';
      case 'illustration':
      case 'photo_and_illustration':
      case 'photography_and_illustration':
        return 'stock_illustration';
      default:
        return null;
    }
  }

  mediaDescription() {
    if (this.isFilm()) {
      return this.t('stock_footage_video');
    }
    if (this.site.isGetty() && this.isEditorial()) {
      return this.t('news_photo');
    }
    const key = this.stockMediaTranslationKey();
    return key ? this.t(key) : '';
  }

  seoThumbUrl() {
    return this.asset.thumbUrl;
  }

  // contentUrl must be the full URL including query parameters — stripping them breaks
  // Google's schema validation and causes rich result eligibility issues.
  // Changes to this function require SEO team sign-off.
  seoContentUrl() {
    if (this.isFilm()) {
      return this.asset.filmPreviewUrl;
    }
    if (this.asset.thumbUrl) {
      return this.asset.thumbUrl;
    }

    return '';
  }

  getCompUrl(url = '') {
    return url.split('?')[0];
  }

  seoLicenseUrl() {
    if (!this.slu) {
      const host = this.site.host.replace(/\/$/, '');
      const path = this.site.isIstock()
        ? this.siteMap.pathRef('licenseAgreement')
        : this.siteMap.pathRef('licenseOverview');
      this.slu = `https://${host}${path}`;
    }
    return this.slu;
  }

  seoAssetLandingUrl() {
    if (!this.salu) {
      const host = this.site.host.replace(/\/$/, '');
      this.salu = `https://${host}${this.siteMap.prefixedPath(
        this.asset.landingUrl
      )}`;
    }
    return this.salu;
  }

  uploadDate() {
    return this.asset.uploadDate;
  }

  artist() {
    return this.asset.artist;
  }

  seoAssetType() {
    if (this.isFilm()) {
      return this.t('stock_footage_video');
    }
    const key = this.stockMediaTranslationKey();
    return key ? this.t(key) : '';
  }

  istockTitle() {
    if (this.isFilm()) {
      return `${this.asset.seoTitle} ${this.t('stock_video')} ${this.asset.id}`;
    }
    switch (this.asset.mediaType.toLowerCase()) {
      case 'photo':
      case 'photography':
        return this.t('stock_photo_title_head', {
          Keyword: this.asset.seoTitle,
        });
      case 'illustration':
      case 'photo_and_illustration':
      case 'photography_and_illustration':
        return `${this.asset.seoTitle} ${this.t('stock_vector_art')} ${
          this.asset.id
        }`;
      default:
        break;
    }
    return '';
  }

  truncateText(text, truncateAt) {
    if (!text) return '';
    if (text.length <= truncateAt) return text;
    const words = text.split(' ');
    const truncatedWords = words.slice(0, truncateAt);
    return `${truncatedWords.join(' ')}...`;
  }

  seoDuration() {
    return this.asset.seoDuration;
  }

  seoVideoFrameSize() {
    return `${this.asset.maxDimensions.width}x${this.asset.maxDimensions.height}`;
  }

  seoVideoQuality() {
    switch (this.asset.filmDefinition) {
      case '4K':
        return '4K';
      case 'HD':
        return 'full HD';
      case 'HD_WEB':
        return 'HD';
      default:
        return 'medium';
    }
  }

  usageInfo() {
    return this.asset.usageInfo;
  }
}
